import React, { useState, useEffect } from 'react';
import { 
  Loader2, X, Plus, Calendar, FileText, 
  CheckCircle2, AlertCircle, ChevronRight, Info, Settings, Trash2, Save, Pencil
} from 'lucide-react';
import { leaveService } from '../../services/leaveService';
import { useAuth } from '../../context/AuthContext';
import StatusBadge from '../../components/StatusBadge';

const LEAVE_TYPES = ['Annual', 'Sick', 'Casual', 'Maternity', 'Paternity', 'Unpaid'];
const STATUS_OPTIONS = ['Pending', 'Approved', 'Rejected', 'Cancelled'];
const POLICY_LEAVE_TYPES = ['Annual', 'Sick', 'Casual', 'Unpaid', 'Maternity', 'Paternity'];

// ===== Modal for Employee to Request Leave (SRS 6.4.2) =====
// Bug fix: employee_id backend model mein required hai — useAuth se le kar bhejo
const RequestLeaveModal = ({ onClose, onDone }) => {
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    const formData = new FormData(e.target);

    const employeeId = user?.employee_id || user?.user_id || user?.id || '';
    if (!employeeId) {
      setError('Employee ID not found. Please re-login.');
      setSubmitting(false);
      return;
    }

    const data = {
      employee_id: employeeId,
      leave_type: formData.get('type'),
      from_date: formData.get('from'),
      to_date: formData.get('to'),
      reason: formData.get('reason')
    };

    try {
      await leaveService.createLeaveRequest(data);
      onDone();
    } catch (err) {
      setError(err.message || 'Failed to submit leave request.');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-card shadow-elevated w-full max-w-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-50 flex items-center justify-between font-bold text-gray-900">
          Apply for Leave
          <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && <div className="p-3 bg-red-50 text-red-700 rounded-lg text-xs">{error}</div>}
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Leave Type</label>
            <select name="type" required className="w-full p-2.5 border rounded-xl bg-gray-50 text-sm">
              {LEAVE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">From Date</label>
              <input type="date" name="from" required className="w-full p-2.5 border rounded-xl text-sm bg-gray-50" />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">To Date</label>
              <input type="date" name="to" required className="w-full p-2.5 border rounded-xl text-sm bg-gray-50" />
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Reason</label>
            <textarea name="reason" required className="w-full p-3 border rounded-xl text-sm bg-gray-50 h-24 outline-none" placeholder="Brief explanation..." />
          </div>
          <button type="submit" disabled={submitting} className="w-full bg-primary text-white py-3 rounded-xl font-bold flex justify-center items-center gap-2 shadow-lg shadow-primary/20">
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Submit Request
          </button>
        </form>
      </div>
    </div>
  );
};
// ===== Modal for HR to Approve/Reject =====
const ReviewModal = ({ leave, onClose, onDone }) => {
  const [status, setStatus] = useState('Approved');
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await leaveService.updateLeave(leave.id, String(status).toLowerCase(), comment);
      onDone();
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-card shadow-elevated w-full max-w-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center font-bold text-gray-900">
          Review Leave Request
          <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-100">
          <p className="text-sm font-bold text-gray-900">{leave.employee_name}</p>
          <p className="text-xs text-gray-500 mt-0.5">{leave.leave_type} &bull; {leave.from_date} to {leave.to_date}</p>
          {leave.reason && (
            <p className="text-xs text-gray-600 mt-2 bg-white p-2 rounded-lg border border-gray-100">
              <span className="font-bold text-gray-400 uppercase text-[10px]">Reason: </span>{leave.reason}
            </p>
          )}
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Decision</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full p-2.5 border rounded-xl text-sm bg-gray-50 focus:ring-2 focus:ring-primary/10">
              <option value="Approved">Approve</option>
              <option value="Rejected">Reject</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-400 uppercase mb-1">Remarks</label>
            <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} className="w-full p-3 border rounded-xl text-sm bg-gray-50 focus:ring-2 focus:ring-primary/10" placeholder="Optional comments..." />
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 text-sm font-bold text-gray-500">Cancel</button>
            <button type="submit" disabled={submitting} className={'flex-1 py-2.5 rounded-xl text-sm font-bold text-white shadow-lg ' + (status === 'Approved' ? 'bg-green-600 shadow-green-100' : 'bg-red-500 shadow-red-100')}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : (status === 'Approved' ? 'Confirm Approval' : 'Confirm Rejection')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Leave = () => {
  const { user } = useAuth();
  // Normalize role values (handles 'HR Manager', 'hr-manager', etc.)
  const roleNorm = user && user.role ? String(user.role).toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_') : '';
  const isAdmin = Boolean(roleNorm && (roleNorm === 'super_admin' || roleNorm === 'hr_manager'));

  const [activeTab, setActiveTab] = useState('requests');
  const [leaves, setLeaves] = useState([]);
  const [statusFilter, setStatusFilter] = useState(isAdmin ? 'pending' : '');
  const [typeFilter, setTypeFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reviewingLeave, setReviewingLeave] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);

  const handleApprove = async (lv) => {
    if (!isAdmin) return;
    if (!window.confirm(`Approve leave for ${lv.employee_name}?`)) return;
    setLoading(true);
    setError('');
    try {
      await leaveService.updateLeave(lv.id, 'approved', null);
      await loadLeaves();
    } catch (err) {
      setError(err.message || 'Failed to approve');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async (lv) => {
    if (!isAdmin) return;
    if (!window.confirm(`Reject leave for ${lv.employee_name}?`)) return;
    setLoading(true);
    setError('');
    try {
      await leaveService.updateLeave(lv.id, 'rejected', null);
      await loadLeaves();
    } catch (err) {
      setError(err.message || 'Failed to reject');
    } finally {
      setLoading(false);
    }
  };

  const loadLeaves = async () => {
    setLoading(true);
    setError('');
    try {
      let res;
      if (isAdmin) {
        res = await leaveService.getAllLeaves(statusFilter || undefined, typeFilter || undefined);
      } else {
        res = await leaveService.getMyLeaves();
      }
      setLeaves(Array.isArray(res) ? res : (res.data || []));
    } catch (err) {
      setError(err.message || 'Failed to fetch records');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadLeaves(); }, [statusFilter, typeFilter, user]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-black text-gray-900 tracking-tight">Leave Management</h1>
          <p className="text-sm text-gray-500 font-medium">{isAdmin ? 'Track and approve staff requests' : 'Manage your own leave applications'}</p>
        </div>
        {!isAdmin && (
          <button onClick={() => setShowRequestModal(true)} className="bg-primary text-white px-6 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 shadow-lg shadow-primary/20 hover:scale-[1.02] transition-all">
            <Plus className="w-4 h-4" /> Apply for Leave
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">
          {error}
        </div>
      )}

      <div className="flex items-center gap-1 border-b border-gray-100 overflow-x-auto no-scrollbar">
        <button onClick={() => setActiveTab('requests')} className={'px-6 py-3 text-sm font-bold border-b-2 transition-all ' + (activeTab === 'requests' ? 'border-primary text-primary' : 'border-transparent text-gray-400')}>
          {isAdmin ? 'All Requests' : 'My Requests'}
        </button>
        <button onClick={() => setActiveTab('balances')} className={'px-6 py-3 text-sm font-bold border-b-2 transition-all ' + (activeTab === 'balances' ? 'border-primary text-primary' : 'border-transparent text-gray-400')}>
          {isAdmin ? 'Staff Balances' : 'My Balance'}
        </button>
        {isAdmin && (
          <button onClick={() => setActiveTab('policy')} className={'px-6 py-3 text-sm font-bold border-b-2 transition-all flex items-center gap-1.5 ' + (activeTab === 'policy' ? 'border-primary text-primary' : 'border-transparent text-gray-400')}>
            <Settings className="w-3.5 h-3.5" /> Policy Settings
          </button>
        )}
      </div>

      {activeTab === 'requests' && (
        <div className="space-y-4">
          {isAdmin && (
            <div className="flex items-center gap-2 flex-wrap">
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-4 py-2 rounded-xl border border-gray-100 text-xs font-bold bg-white shadow-soft outline-none focus:ring-1 focus:ring-primary">
                <option value="">All Statuses</option>
                {STATUS_OPTIONS.map(s => <option key={s} value={s.toLowerCase()}>{s}</option>)}
              </select>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary opacity-20 w-10 h-10" /></div>
          ) : leaves.length === 0 ? (
            <div className="bg-white p-12 text-center rounded-card border border-dashed border-gray-200">
               <Info className="w-10 h-10 text-gray-200 mx-auto mb-3" />
               <p className="text-gray-400 font-medium tracking-tight">No leave records found here.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3">
              {leaves.map((lv) => (
                <div key={lv.id} className="bg-white p-5 rounded-card border border-gray-100 shadow-soft flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-primary/5 rounded-lg flex items-center justify-center text-primary"><FileText className="w-5 h-5" /></div>
                    <div>
                      <h3 className="font-bold text-gray-900 leading-none">{lv.leave_type} Leave</h3>
                      <p className="text-[10px] font-bold text-gray-400 uppercase mt-1 tracking-widest">{lv.from_date} to {lv.to_date} &bull; {lv.total_days} Day(s)</p>
                      {isAdmin && <p className="text-xs font-medium text-primary/70 mt-1 uppercase tracking-tighter">Request by: {lv.employee_name}</p>}
                      {lv.reason && <p className="text-xs text-gray-500 mt-1">{lv.reason}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <StatusBadge status={lv.status} />
                    {isAdmin && (lv.status || '').toLowerCase() === 'pending' && (
                      <>
                        <button onClick={() => handleApprove(lv)} className="px-3 py-1 bg-green-600 text-white rounded-md text-xs font-semibold hover:opacity-90">Approve</button>
                        <button onClick={() => handleReject(lv)} className="px-3 py-1 bg-red-500 text-white rounded-md text-xs font-semibold hover:opacity-90">Reject</button>
                        <button onClick={() => setReviewingLeave(lv)} className="p-2 bg-gray-50 text-primary hover:bg-primary hover:text-white rounded-lg transition-all"><ChevronRight className="w-4 h-4" /></button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'balances' && <LeaveBalancesTab isAdmin={isAdmin} user={user} />}
      {activeTab === 'policy' && isAdmin && <LeavePolicyTab />}

      {showRequestModal && <RequestLeaveModal onClose={() => setShowRequestModal(false)} onDone={() => { setShowRequestModal(false); loadLeaves(); }} />}
      {reviewingLeave && <ReviewModal leave={reviewingLeave} onClose={() => setReviewingLeave(null)} onDone={() => { setReviewingLeave(null); loadLeaves(); }} />}
    </div>
  );
};

const LeaveBalancesTab = ({ isAdmin, user }) => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(isAdmin ? '' : (user?.employee_id || user?.user_id || user?.id));
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [balanceError, setBalanceError] = useState('');

  useEffect(() => {
    const loadData = async () => {
      if (isAdmin) {
        const { employeeService } = await import('../../services/employeeService');
        const res = await employeeService.getEmployees(undefined, 'active');
        setEmployees(res.data || []);
      } else if (user) {
        setSelectedEmployee(user.employee_id || user.user_id || user.id);
        await handleCheck();
      }
    };
    loadData();
  }, [isAdmin, user]);

  const handleCheck = async () => {
    const idToCheck = isAdmin ? selectedEmployee : (user?.employee_id || user?.user_id || user?.id);
    if (!idToCheck) {
      setBalance(null);
      setBalanceError('Your employee identity is not available yet. Please refresh or re-login.');
      return;
    }
    setLoading(true);
    setBalanceError('');
    try {
      const res = await leaveService.getLeaveBalance(idToCheck);
      const balanceData = res?.data || res;
      setBalance(balanceData);
    } catch (err) {
      setBalance(null);
      setBalanceError(err.message || 'Unable to load leave balance.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {isAdmin && (
        <div className="bg-white rounded-card shadow-soft border border-gray-100 p-5 flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-48">
            <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1.5">Select Employee</label>
            <select value={selectedEmployee} onChange={(e) => setSelectedEmployee(e.target.value)} className="w-full p-2.5 border rounded-xl text-sm bg-gray-50">
              <option value="">Choose...</option>
              {employees.map(e => <option key={e.employee_id} value={e.employee_id}>{e.full_name}</option>)}
            </select>
          </div>
          <button onClick={handleCheck} disabled={!selectedEmployee || loading} className="bg-primary text-white px-6 py-2.5 rounded-xl text-sm font-bold shadow-lg shadow-primary/20">
            Check Balance
          </button>
        </div>
      )}

      {balanceError && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-900 rounded-xl p-4 text-sm">
          {balanceError}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="animate-spin text-primary opacity-20 w-8 h-8" /></div>
      ) : balance ? (
        <div className="bg-white rounded-card shadow-soft border border-gray-100 p-8 space-y-6">
          <div className="flex items-center justify-between border-b pb-4">
             <h2 className="text-sm font-black text-gray-900 uppercase tracking-widest">Available Quotas ({balance.year})</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(balance.balances).map(([type, b]) => (
              <div key={type} className="p-4 bg-gray-50 rounded-2xl border border-gray-100 flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-black text-gray-400 uppercase">{type}</p>
                  <p className="text-2xl font-black text-gray-900 mt-1">{b.remaining}</p>
                </div>
                <div className="text-right">
                   <p className="text-[10px] font-bold text-gray-300 uppercase">USED: {b.used}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

// ===== Feature: Dynamic Leave Management - HR/Admin configuration panel =====
const LeavePolicyTab = () => {
  const [policies, setPolicies] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');

  const [scope, setScope] = useState('global');
  const [department, setDepartment] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [period, setPeriod] = useState('Yearly');
  const [quotas, setQuotas] = useState({ Annual: 15, Sick: 10, Casual: 5, Unpaid: 0, Maternity: 90, Paternity: 14 });
  const [editingPolicyId, setEditingPolicyId] = useState(null);

  const handleEditPolicy = (p) => {
    setEditingPolicyId(p.id);
    setScope(p.scope);
    setDepartment(p.department || '');
    setEmployeeId(p.employee_id || '');
    setPeriod(p.period || 'Yearly');
    setQuotas({
      Annual: p.quotas?.Annual ?? 15,
      Sick: p.quotas?.Sick ?? 10,
      Casual: p.quotas?.Casual ?? 5,
      Unpaid: 0,
      Maternity: p.quotas?.Maternity ?? 90,
      Paternity: p.quotas?.Paternity ?? 14,
    });
  };

  const handleCancelEdit = () => {
    setEditingPolicyId(null);
    setScope('global');
    setDepartment('');
    setEmployeeId('');
    setPeriod('Yearly');
    setQuotas({ Annual: 15, Sick: 10, Casual: 5, Unpaid: 0, Maternity: 90, Paternity: 14 });
  };

  const loadPolicies = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await leaveService.getLeavePolicies();
      setPolicies(Array.isArray(res) ? res : (res.data || []));
    } catch (err) {
      setError(err.message || 'Failed to load leave policies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPolicies();
    const loadLookups = async () => {
      try {
        const { employeeService } = await import('../../services/employeeService');
        const [deptRes, empRes] = await Promise.all([
          employeeService.getDepartments(),
          employeeService.getEmployees(undefined, 'active')
        ]);
        const deptArray = Array.isArray(deptRes) ? deptRes : (deptRes.data || deptRes.departments || []);
        setDepartments(deptArray);
        setEmployees(empRes.data || []);
      } catch (err) {
        console.error('Lookup load error:', err);
      }
    };
    loadLookups();
  }, []);

  const handleQuotaChange = (type, value) => {
    setQuotas((prev) => ({ ...prev, [type]: value === '' ? null : parseFloat(value) }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaveError('');
    setSaveSuccess('');

    if (scope === 'department' && !department) {
      setSaveError('Please select a department.');
      return;
    }
    if (scope === 'employee' && !employeeId) {
      setSaveError('Please select an employee.');
      return;
    }

    setSaving(true);
    try {
      await leaveService.saveLeavePolicy({
        scope,
        department: scope === 'department' ? department : null,
        employee_id: scope === 'employee' ? employeeId : null,
        period,
        quotas: {
          Annual: quotas.Annual,
          Sick: quotas.Sick,
          Casual: quotas.Casual,
          Unpaid: null,
          Maternity: quotas.Maternity,
          Paternity: quotas.Paternity
        }
      });
    setSaveSuccess('Leave policy saved successfully.');
      setEditingPolicyId(null);
      await loadPolicies();
    } catch (err) {
      setSaveError(err.message || 'Failed to save leave policy');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (policyId) => {
    if (!window.confirm('Remove this leave policy? It will fall back to the next level up.')) return;
    try {
      await leaveService.deleteLeavePolicy(policyId);
      loadPolicies();
    } catch (err) {
      setError(err.message || 'Failed to delete policy');
    }
  };

  const describeScope = (p) => {
    if (p.scope === 'global') return 'Global (applies to everyone)';
    if (p.scope === 'department') return 'Department: ' + p.department;
    if (p.scope === 'employee') {
      const emp = employees.find((e) => e.employee_id === p.employee_id);
      return 'Employee: ' + (emp ? emp.full_name : p.employee_id);
    }
    return p.scope;
  };

  return (
    <div className="space-y-6">
      {/* Configuration Form */}
      <div className="bg-white rounded-card shadow-soft border border-gray-100 p-6">
        <h2 className="text-sm font-black text-gray-900 uppercase tracking-widest mb-1">Configure Leave Allocation</h2>
        <p className="text-xs text-gray-500 mb-5">Set leave day quotas globally, per department, or for one specific employee. A more specific scope always overrides a broader one.</p>

        {saveError && <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm">{saveError}</div>}
        {saveSuccess && <div className="mb-4 bg-green-50 border border-green-200 text-green-700 rounded-xl p-3 text-sm">{saveSuccess}</div>}

        <form onSubmit={handleSave} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Scope</label>
              <select value={scope} onChange={(e) => setScope(e.target.value)} className="w-full p-2.5 border rounded-xl bg-gray-50 text-sm">
                <option value="global">Global (everyone)</option>
                <option value="department">Department</option>
                <option value="employee">Individual Employee</option>
              </select>
            </div>

            {scope === 'department' && (
              <div>
                <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Department</label>
                <select value={department} onChange={(e) => setDepartment(e.target.value)} className="w-full p-2.5 border rounded-xl bg-gray-50 text-sm">
                  <option value="">Select department</option>
                  {departments.map((d) => {
                    const name = d.name || d.department_name || d.dept_name || d.code || d;
                    return <option key={name} value={name}>{name}</option>;
                  })}
                </select>
              </div>
            )}

            {scope === 'employee' && (
              <div>
                <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Employee</label>
                <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} className="w-full p-2.5 border rounded-xl bg-gray-50 text-sm">
                  <option value="">Select employee</option>
                  {employees.map((e) => <option key={e.employee_id} value={e.employee_id}>{e.full_name}</option>)}
                </select>
              </div>
            )}

            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Period</label>
              <select value={period} onChange={(e) => setPeriod(e.target.value)} className="w-full p-2.5 border rounded-xl bg-gray-50 text-sm">
                <option value="Yearly">Yearly (enter annual total)</option>
                <option value="Monthly">Monthly (enter per-month allowance)</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-bold text-gray-400 uppercase mb-2">Leave Day Quotas</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {POLICY_LEAVE_TYPES.filter((t) => t !== 'Unpaid').map((type) => (
                <div key={type}>
                  <label className="block text-[10px] text-gray-400 mb-1">{type}</label>
                  <input
                    type="number"
                    min="0"
                    value={quotas[type] ?? ''}
                    onChange={(e) => handleQuotaChange(type, e.target.value)}
                    className="w-full p-2.5 border rounded-xl bg-gray-50 text-sm"
                  />
                </div>
              ))}
              <div>
                <label className="block text-[10px] text-gray-400 mb-1">Unpaid</label>
                <input type="text" disabled value="Unlimited" className="w-full p-2.5 border rounded-xl bg-gray-100 text-sm text-gray-400" />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button type="submit" disabled={saving} className="bg-primary text-white px-6 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 shadow-lg shadow-primary/20 disabled:opacity-60">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {editingPolicyId ? 'Update Policy' : 'Save Policy'}
            </button>
            {editingPolicyId && (
              <button type="button" onClick={handleCancelEdit} className="px-4 py-2.5 rounded-xl text-sm font-bold text-gray-500 border border-gray-200">
                Cancel Edit
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Existing Policies */}
      <div className="bg-white rounded-card shadow-soft border border-gray-100 p-6">
        <h2 className="text-sm font-black text-gray-900 uppercase tracking-widest mb-4">Configured Policies</h2>
        {error && <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm">{error}</div>}
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="animate-spin text-primary opacity-20 w-8 h-8" /></div>
        ) : policies.length === 0 ? (
          <p className="text-sm text-gray-400 py-4">No custom policies configured yet. Default quotas (Annual 15, Sick 10, Casual 5) apply to everyone.</p>
        ) : (
          <div className="space-y-2">
            {policies.map((p) => (
              <div key={p.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100">
                <div>
                  <p className="text-sm font-bold text-gray-900">{describeScope(p)}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {p.period} &bull; {Object.entries(p.quotas || {}).filter(([, v]) => v !== null).map(([k, v]) => k + ': ' + v).join(', ')}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => handleEditPolicy(p)} className="p-2 text-gray-400 hover:text-primary" title="Edit policy">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(p.id)} className="p-2 text-gray-400 hover:text-red-500" title="Remove policy">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Leave;